import { Button } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import {
  AxiosResult,
  AxiosResultDefaultError,
} from '../../../../../api/request';
import Loading from '../../../../../components/info/Loading';
import {
  Subsidy,
  CreateSubsidyData,
  createSubsidyRequest,
  updateSubsidyRequest,
  getSubsidyByIdRequest,
  getPeriodsForSubsidyRequest,
} from '../../api/subsidy';
import { checkCurrencyAsNumber } from '../../../../../utils/currency';

import SubsidyDataSection from './sections/SubsidyDataSection';
import SubsidyPeriodsSection from './sections/SubsidyPeriodsSection';

const prefix = 'tariffAndTuition.subsidies';

interface SubsidyFormProps {
  recordToEdit: Subsidy | null;
  onSubmit: (result: AxiosResult<Subsidy, AxiosResultDefaultError>) => void;
  onCancel: () => void;
}

interface FormValues {
  name: string;
  applyAutomatically: boolean;
  periodAmounts: { [key: number]: string };
}

export const SubsidyForm = ({
  recordToEdit,
  onSubmit: onSubmitProp,
  onCancel,
}: SubsidyFormProps) => {
  const { t } = useTranslation();
  const editing = !!recordToEdit;

  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState<any[]>([]);
  const [yearFilter, setYearFilter] = useState('last5');
  const [loadedAmounts, setLoadedAmounts] = useState<{ [key: number]: string }>(
    {},
  );

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      applyAutomatically: false,
      periodAmounts: {},
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const loadPeriods = useCallback(async () => {
    const { data } = await getPeriodsForSubsidyRequest(yearFilter);
    if (data) {
      setPeriods(data);
    }
  }, [yearFilter]);

  const loadSubsidyData = useCallback(async () => {
    if (recordToEdit) {
      const { data } = await getSubsidyByIdRequest(recordToEdit.id);
      if (data) {
        setValue('name', data.name);
        setValue('applyAutomatically', data.applyAutomatically ?? false);
        const amounts: { [key: number]: string } = {};
        data.periodAmounts?.forEach((pa) => {
          amounts[pa.periodId] = String(Math.round(pa.amount));
        });
        setLoadedAmounts(amounts);
      }
    }
    setLoading(false);
  }, [recordToEdit, setValue]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  useEffect(() => {
    loadSubsidyData();
  }, [loadSubsidyData]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const periodAmounts = Object.entries(values.periodAmounts ?? {})
        .filter(([, amount]) => amount && checkCurrencyAsNumber(amount) > 0)
        .map(([periodId, amount]) => ({
          periodId: parseInt(periodId),
          amount: checkCurrencyAsNumber(amount),
        }));

      const payload: CreateSubsidyData = {
        name: values.name,
        applyAutomatically: values.applyAutomatically,
        periodAmounts,
      };

      const result = editing
        ? await updateSubsidyRequest(recordToEdit!.id, payload)
        : await createSubsidyRequest(payload);

      onSubmitProp(result);
    },
    [editing, recordToEdit, onSubmitProp],
  );

  if (loading) {
    return <Loading insideCard />;
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)} className="p-4">
        {/* Header with back button */}
        <Row className="mb-4">
          <Col>
            <Button
              type="button"
              text={t(`${prefix}.backToList`)}
              outlined
              icon="back"
              onClick={onCancel}
              size="sm"
              className="rounded-pill"
            />
          </Col>
        </Row>

        {/* Description text */}
        <Row className="mb-4">
          <Col xs="12">
            <p className="text-muted mb-0">{t(`${prefix}.formDescription`)}</p>
          </Col>
        </Row>

        <SubsidyDataSection />

        <SubsidyPeriodsSection
          periods={periods}
          loadedAmounts={loadedAmounts}
          yearFilter={yearFilter}
          onYearFilterChange={setYearFilter}
        />

        {/* Action buttons */}
        <Row className="mt-4">
          <Col xs="12" className="d-flex justify-content-end">
            <Button
              type="button"
              text={t('common.actions.cancel')}
              outlined
              onClick={onCancel}
              className="mr-3"
            />
            <Button
              type="submit"
              text={t('common.actions.saveChanges')}
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};
